import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { toast } from "@outpave/ds";
import allocationValidation from "../validations/allocation";
import { ADD_ALLOCATION, UPDATE_ALLOCATION } from "../graphql/allocation";
import { useEffect } from "react";
import { AllocationIdAndTypeProps } from "components/common/AllocationSearchAutoComplete";

const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export interface FormikAllocation {
  allocationId: string;
  allocationType: {
    key: string;
    name: string;
  };
  allocationCode: string;
  parentAllocation: {
    id: string;
    allocationCode: string;
  };
}

const useAllocationForm = (
  afterAllocationSubmit: (
    allocationIdAndType: AllocationIdAndTypeProps,
  ) => void,
) => {
  const [addAllocation, { loading: addingAllocation }] =
    useMutation(ADD_ALLOCATION);
  const [updateAllocation, { loading: updatingAllocation }] =
    useMutation(UPDATE_ALLOCATION);

  const formikAllocation = useFormik<FormikAllocation>({
    initialValues: {
      allocationId: "",
      allocationType: {
        key: "",
        name: "",
      },
      allocationCode: "",
      parentAllocation: { id: "", allocationCode: "" },
    },
    validationSchema: allocationValidation,
    onSubmit: async (values) => {
      const { allocationId, allocationType } = values;
      try {
        let updatedAllocationId: string;

        if (allocationId) {
          updatedAllocationId = await handleUpdateAllocation(values);
        } else {
          updatedAllocationId = await handleAddAllocation(values);
        }
        formikAllocation.resetForm();

        const allocationIdAndType: AllocationIdAndTypeProps = {
          allocationId: updatedAllocationId,
          allocationType: allocationType,
          allocationCode: values.allocationCode,
        };

        afterAllocationSubmit(allocationIdAndType);
      } catch (error: unknown) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    formikAllocation.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikAllocation.values]);

  async function handleUpdateAllocation(
    allocation: FormikAllocation,
  ): Promise<string> {
    try {
      const parentAllocationId = allocation.parentAllocation?.id || "";
      const input = buildAllocationInput(allocation, parentAllocationId);
      const updatedAllocation = await updateAllocation({
        variables: {
          id: allocation.allocationId,
          input: input,
        },
      });
      toast.success("Allocation updated successfully");
      return updatedAllocation.data.updateAllocation.id;
    } catch (error: unknown) {
      toast.error("Error updating allocation: " + messageOf(error));
      throw error;
    }
  }

  async function handleAddAllocation(
    allocation: FormikAllocation,
  ): Promise<string> {
    try {
      const parentAllocationId = allocation.parentAllocation?.id || "";
      const input = buildAllocationInput(allocation, parentAllocationId);
      const addedAllocation = await addAllocation({
        variables: {
          input: input,
        },
      });
      toast.success("Allocation added successfully");
      return addedAllocation.data.addAllocation.id;
    } catch (error: unknown) {
      toast.error("Error adding allocation: " + messageOf(error));
      throw error;
    }
  }

  function buildAllocationInput(
    allocation: FormikAllocation,
    parentAllocationId: string,
  ) {
    return {
      allocationType: {
        key: allocation.allocationType.key,
        name: allocation.allocationType.name,
      },
      allocationCode: allocation.allocationCode,
      parentAllocationId: parentAllocationId,
    };
  }

  return {
    formikAllocation,
    addingAllocation,
    updatingAllocation,
  };
};

export default useAllocationForm;
